import { useState, useEffect, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { useBranchofficesByEvent } from '../../context/BranchofficesByEventContext';
import { createPortal } from "react-dom";
import { EventsFetchAPI } from "../../api/Events";
import { EcommerceContentFetchAPI } from "../../api/EcommerceContent";
import { AuthFetchAPI } from "../../api/Auth";

import { useNavigate, useParams } from "react-router-dom";
import { buildCloudinaryImageUrl } from '../../utils/CloudinaryImageUrl';

const STORAGE_KEY = "donlee_first_visit";
const SELECTED_KEY = "donlee_active_event";
const SKIP_UNTIL_KEY = "donlee_event_skip_until";
const EVENT_SKIP_MS = 24 * 60 * 60 * 1000;

/* ══════════════════════════════════════════
   SVG HELPERS
══════════════════════════════════════════ */
const IconCalendar = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);
const IconChevron = () => (
    <svg className="dl-btn-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="6 9 12 15 18 9" />
    </svg>
);
const IconCheck = ({ size = 14, color = "currentColor", weight = "2" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={weight}>
        <polyline points="20 6 9 17 4 12" />
    </svg>
);
const IconClock = () => (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
);
const IconPin = () => (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    </svg>
);
const IconClose = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

/* ══════════════════════════════════════════
   SUB-COMPONENTS
══════════════════════════════════════════ */
function EventCard({ event, isActive, onSelect }) {
    const { t } = useTranslation();
    return (
        <div
            className={`dl-event-card notranslate${isActive ? " active" : ""}`}
            translate="no"
            onClick={() => onSelect(event.id)}
        >
            {/* Image */}
            <div className="dl-card-img">
                <img src={buildCloudinaryImageUrl({ url: event.img, width: 300, quality: "auto", type: "image" }) || undefined} alt={event.name} onError={e => (e.target.style.opacity = 0)} />
                {event.live && (
                    <div className="dl-live-badge">
                        <span className="dl-badge-dot" />
                        {t('events.live')}
                    </div>
                )}
                {!isActive && <div className="dl-cat-badge">{event.category}</div>}
                {isActive && (
                    <div className="dl-check">
                        <IconCheck size={12} color="white" weight="2.5" />
                    </div>
                )}
                <div className="dl-select-overlay">
                    <IconCheck size={12} color="white" weight="2.5" />
                    {isActive ? t('events.selected') : t('events.select')}
                </div>
            </div>

            {/* Body */}
            <div className="dl-card-body">
                {/*<div className="dl-card-logo">{event.logo}</div>*/}
                <p className="dl-card-name">{event.name}</p>
                <p className="dl-card-desc">{event.desc}</p>
                <div className="dl-card-meta">
                    <span className="dl-meta-chip">
                        <IconClock /> {event.date}
                    </span>
                    <span className="dl-meta-chip">
                        <IconPin /> {event.location}
                    </span>
                </div>
            </div>
        </div>
    );
}

/* ══════════════════════════════════════════
   TOAST HOOK
══════════════════════════════════════════ */
function useToast() {
    const [toast, setToast] = useState({ visible: false, msg: "", dot: "#4ade80" });
    const timerRef = { current: null };

    const showToast = useCallback((msg, dot = "#4ade80") => {
        if (timerRef.current) clearTimeout(timerRef.current);
        setToast({ visible: true, msg, dot });
        timerRef.current = setTimeout(() => setToast(t => ({ ...t, visible: false })), 3000);
    }, []);

    return { toast, showToast };
}


// Ecommerce content config
const getEcommerceEventsConfig = () => {
    try {
        const ecommerceContent = JSON.parse(localStorage.getItem("ecommerceContent"));
        return ecommerceContent?.ui_config?.events || {
            enabled: false,
            require_access_code: false,
            require_stand: false
        };
    } catch (error) {
        return {
            enabled: false,
            require_access_code: false,
            require_stand: false
        };
    }
};

const getBusinessPrimaryColor = () => {
    const cssColor = getComputedStyle(document.documentElement).getPropertyValue("--color-primary").trim();
    if (cssColor) return cssColor;

    try {
        const generalData = JSON.parse(localStorage.getItem("generalData"));
        return generalData?.[0]?.data?.configuration?.containerColor || "#111827";
    } catch (error) {
        return "#111827";
    }
};

const getStoredBusiness = () => {
    try {
        const business = localStorage.getItem("business");
        return business && business !== "undefined" && business !== "null" ? JSON.parse(business) : null;
    } catch (error) {
        return null;
    }
};

const getStoredEventData = () => {
    try {
        const eventData = localStorage.getItem("dataEvent");
        return eventData && eventData !== "undefined" && eventData !== "null" ? JSON.parse(eventData) : null;
    } catch (error) {
        return null;
    }
};

const hasUsableToken = (value) => {
    return value && value !== "undefined" && value !== "null";
};

const ensurePublicToken = async (business) => {
    if (!business?.id) return "";

    try {
        const response = await AuthFetchAPI.validToken();
        if (response.data === true) {
            return true;
        }
    } catch (error) {
        // Generate a fresh public token below.
    }

    try {
        const response = await AuthFetchAPI.generatePublicToken(business.id);
        if (response?.data?.success === false) return "";
        // The cookie this endpoint also sets is cross-origin (frontend and API are different
        // hosts) - browsers increasingly refuse to store/send third-party cookies (always in
        // Incognito/InPrivate), so relying on it alone left every call below with no credentials
        // at all, 401ing every time. The response already carries the same token in its body -
        // return it so callers can send it explicitly via x-access-token instead.
        return response?.data?.token || "";
    } catch (error) {
        return "";
    }
};

const extractBranchLinksFromEvent = (event) => {
    const collected = [];
    const seen = new Set();
    const branchIdKeys = [
        "branchoffice_id",
        "branchofficeId",
        "branch_office_id",
        "branchOfficeId",
        "branch_id",
        "branchId",
        "sucursal_id",
        "sucursalId",
    ];
    const branchCodeKeys = [
        "branchoffice_code",
        "branchofficeCode",
        "branch_office_code",
        "branchOfficeCode",
        "branch_code",
        "branchCode",
        "sucursal_code",
        "sucursalCode",
    ];
    const branchArrayKeys = [
        "branchoffices",
        "branch_offices",
        "branches",
        "event_branchoffices",
        "branchoffices_by_event",
        "sucursales",
    ];

    const addBranchLink = (value, sourceKey = "") => {
        if (value === null || value === undefined || value === "") return;

        const normalized = typeof value === "object" ? value : { branchoffice_id: value };
        const id = branchIdKeys.map((key) => normalized?.[key]).find((item) => item !== undefined && item !== null && item !== "");
        const code = branchCodeKeys.map((key) => normalized?.[key]).find((item) => item !== undefined && item !== null && item !== "");

        if (!id && !code && !sourceKey.toLowerCase().includes("branch") && !sourceKey.toLowerCase().includes("sucursal")) return;

        const key = `${id || ""}:${code || ""}:${JSON.stringify(normalized)}`;
        if (seen.has(key)) return;
        seen.add(key);
        collected.push(normalized);
    };

    const visit = (value, sourceKey = "") => {
        if (!value) return;

        if (Array.isArray(value)) {
            value.forEach((item) => {
                if (typeof item === "object") {
                    addBranchLink(item, sourceKey);
                    visit(item, sourceKey);
                } else if (sourceKey.toLowerCase().includes("branch") || sourceKey.toLowerCase().includes("sucursal")) {
                    addBranchLink(item, sourceKey);
                }
            });
            return;
        }

        if (typeof value !== "object") return;

        if (
            branchIdKeys.some((key) => value[key] !== undefined && value[key] !== null && value[key] !== "") ||
            branchCodeKeys.some((key) => value[key] !== undefined && value[key] !== null && value[key] !== "")
        ) {
            addBranchLink(value, sourceKey);
        }

        Object.entries(value).forEach(([key, child]) => {
            if (branchArrayKeys.includes(key) || key.toLowerCase().includes("branch") || key.toLowerCase().includes("sucursal")) {
                visit(child, key);
            }
        });
    };

    visit(event);
    visit(event?.data);

    return collected;
};

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
export default function EventSelector() {
    const { t } = useTranslation();
    const { setBranchofficesByEvent } = useBranchofficesByEvent();
    const navigate = useNavigate();
    const { quote, code, PreOrder_id, keyproduct } = useParams();
    const [isOpen, setIsOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(() => localStorage.getItem(SELECTED_KEY) || null);
    const [pendingId, setPendingId] = useState(null);
    const [hasVisited, setHasVisited] = useState(() => !!localStorage.getItem(SELECTED_KEY));
    const { toast, showToast } = useToast();
    const [events, setEvents] = useState([]);
    const [selectedEventData, setSelectedEventData] = useState(() => getStoredEventData());
    const [eventsConfig, setEventsConfig] = useState(() => getEcommerceEventsConfig());
    const [brandColor, setBrandColor] = useState(() => getBusinessPrimaryColor());
    const now = new Date();
    // Estado para saber si los eventos están habilitados
    const [eventsEnabled, setEventsEnabled] = useState(() => {
        const business = getStoredBusiness();
        return business?.events_enabled === 1;
    });
    //Modal confirmation
    const [showAccessCodeStep, setShowAccessCodeStep] = useState(false);
    const [accessCodeInput, setAccessCodeInput] = useState("");
    const [accessCodeError, setAccessCodeError] = useState("");

    const clearEventSelection = () => {
        localStorage.removeItem(SELECTED_KEY);
        localStorage.removeItem("dataEvent");
        localStorage.removeItem("eventRules");
        localStorage.removeItem("branchofficesByEvent");
        setBranchofficesByEvent(null);
        setSelectedId(null);
        setSelectedEventData(null);
    };

    const isEventSelectedValid = () => {
        const eventSelected = localStorage.getItem('dataEvent');
        if (eventSelected) {
            try {
                const eventObj = JSON.parse(eventSelected);


                const now = new Date();
                // Nuevo formato: el evento tiene initialDate y maxDate directamente
                if (eventObj.maxDate) {
                    const eventEnd = new Date(eventObj.maxDate);
                    return eventEnd >= now;
                }
            } catch (e) {
                return false;
            }
        }
        return false;
    };

    const getEvenetsBybusiness = async (direct) => {
        let business = getStoredBusiness();
        let logo = localStorage.getItem("logo");
        const hasPublicToken = await ensurePublicToken(business);
        if (!hasPublicToken) return;
        const publicToken = hasPublicToken === true ? undefined : hasPublicToken;
        if (!business?.events_enabled) return; // Solo si está habilitado



        if (isEventSelectedValid() == false || direct == true) {
            const shouldOpenEventPrompt = true;

            if (direct == true) {
                localStorage.removeItem(STORAGE_KEY);
                localStorage.removeItem(SKIP_UNTIL_KEY);
            }

            clearEventSelection();
            setHasVisited(!shouldOpenEventPrompt);
            setIsOpen(shouldOpenEventPrompt);

            try {
                const res = await EventsFetchAPI.getEventsByBusiness(business.id, publicToken);
                let events = res.data.events
                // ...existing code...
                // Filtrar eventos activos por fecha y estado (en curso o futuros)
                const eventosActivos = events.filter(event =>
                    event.state === 1 &&
                    event.dates.some(dateObj => {
                        const inicio = new Date(dateObj.initialDate);
                        const fin = new Date(dateObj.maxDate);
                        // En curso o futuros
                        return (now >= inicio && now <= fin) || (now < inicio);
                    })
                );
                // Mapear a la estructura deseada
                const eventosFormateados = eventosActivos.map(evento => {
                    // Tomar la primera fecha activa o futura
                    const fechaActiva = evento.dates.find(dateObj => {
                        const inicio = new Date(dateObj.initialDate);
                        const fin = new Date(dateObj.maxDate);
                        return (now >= inicio && now <= fin) || (now < inicio);
                    });
                    return {
                        id: evento.id,
                        name: evento.name,
                        desc: evento.description,
                        access_code: evento.code,
                        category: "Feria", // Puedes ajustar según tu lógica
                        live: now >= new Date(fechaActiva.initialDate) && now <= new Date(fechaActiva.maxDate),
                        date: fechaActiva ? `${fechaActiva.initialDate.split('T')[0]} · ${fechaActiva.maxDate.split('T')[0]}` : "",
                        maxDate: fechaActiva ? fechaActiva.maxDate : null,
                        initialDate: fechaActiva ? fechaActiva.initialDate : null,
                        location: "Todas las sucursales", // Puedes ajustar según tu lógica
                        img: evento.img || logo,
                        branchoffices: extractBranchLinksFromEvent(evento),
                        data: evento
                    };
                });
                // Ordenar por fecha inicial en incremento
                eventosFormateados.sort((a, b) => {
                    const fechaA = a.date ? new Date(a.date.split('·')[0].trim()) : new Date(0);
                    const fechaB = b.date ? new Date(b.date.split('·')[0].trim()) : new Date(0);
                    return fechaA - fechaB;
                });

                setEvents(eventosFormateados);
            } catch (err) {

            }

        }
    }

    const getEcommerceContent = async () => {
        let business = getStoredBusiness();
        if (!business) return;
        const token = await ensurePublicToken(business);
        if (!token) return;
        const publicToken = token === true ? undefined : token;
        try {
            const res = await EcommerceContentFetchAPI.getEcommerceContentByBusiness(business.id, publicToken);
            localStorage.setItem("ecommerceContent", JSON.stringify(res.data.data));
            setEventsConfig(res.data.data?.ui_config?.events || {
                enabled: false,
                require_access_code: false,
                require_stand: false
            });
        } catch (err) {
            localStorage.setItem("ecommerceContent", null);
            setEventsConfig({
                enabled: false,
                require_access_code: false,
                require_stand: false
            });
        }
    }


    const getBranchByEvent = async (event) => {
        const eventId = typeof event === "object" ? event?.id : event;
        const fallbackBranches = typeof event === "object" ? extractBranchLinksFromEvent(event) : [];
        const business = getStoredBusiness();
        const token = await ensurePublicToken(business);
        if (!token) return fallbackBranches;
        const publicToken = token === true ? undefined : token;
        try {
            const res = await EventsFetchAPI.getBranchByEvent(eventId, publicToken);
            const eventBranches = Array.isArray(res.data.branchoffices) && res.data.branchoffices.length > 0
                ? res.data.branchoffices
                : fallbackBranches;

            setBranchofficesByEvent(eventBranches);
            return eventBranches;
        } catch (err) {

            setBranchofficesByEvent(fallbackBranches);
            return fallbackBranches;
        }
    }





    /* Auto-open on first visit */
    useEffect(() => {
        if (!hasVisited && !isOpen) {
            const t = setTimeout(() => {
                setBrandColor(getBusinessPrimaryColor());
                setIsOpen(true)
            }, 400);
            return () => clearTimeout(t);
        }
    }, [isOpen, hasVisited]);

    /* Lock body scroll when modal open and eventsEnabled */
    useEffect(() => {
        if (isOpen && eventsEnabled) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => { document.body.style.overflow = ""; };
    }, [isOpen, eventsEnabled]);

    /* Escape key */
    useEffect(() => {
        const handler = (e) => {
            if (e.key === "Escape" && (hasVisited || selectedId)) closeModal();
        };
        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, [hasVisited, selectedId]);

    // Escuchar cambios en business en localStorage
    useEffect(() => {
        const checkBusiness = () => {
            const business = getStoredBusiness();
            setEventsEnabled(business?.events_enabled === 1);
        };
        // Polling cada 1s
        const interval = setInterval(checkBusiness, 1000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (eventsEnabled) {
            getEvenetsBybusiness(); // Solo si está habilitado
            getEcommerceContent();
        } else {
            setEvents([]); // Limpiar eventos si se deshabilita
            localStorage.removeItem("eventRules");
        }
    }, [eventsEnabled]);

    useEffect(() => {
        // Cuando los eventos se cargan, verifica si hay un evento guardado en localStorage y actualiza selectedId si existe
        if (events.length > 0) {
            const storedId = localStorage.getItem(SELECTED_KEY);
            if (storedId && events.some(e => e.id === storedId)) {
                setSelectedId(storedId);
            }
        }
    }, [events]);



    function openModal() {
        if (!eventsEnabled) return; // No abrir si no está habilitado
        setBrandColor(getBusinessPrimaryColor());
        setPendingId(null);
        setIsOpen(true);
    }

    function closeModal() {
        setIsOpen(false);
        setShowAccessCodeStep(false);
        setAccessCodeInput("");
        setAccessCodeError("");
    }

    function handleOverlayClick(e) {
        if (e.target === e.currentTarget && (hasVisited || selectedId)) closeModal();
    }

    function handleSelect(id) {
        setPendingId(prev => (String(prev) === String(id) ? null : String(id)));
        setShowAccessCodeStep(false);
        setAccessCodeInput("");
        setAccessCodeError("");
    }

    const getEventRules = async (event_id) => {
        const business = getStoredBusiness();
        const token = await ensurePublicToken(business);
        if (!token) return [];
        const publicToken = token === true ? undefined : token;
        try {
            const res = await EventsFetchAPI.getEventRules(event_id, publicToken);

            // Adjust this according to your API response structure
            const rules = res?.data?.rules || res?.data?.data || res?.data || [];

            localStorage.setItem("eventRules", JSON.stringify(rules));
            return rules;
        } catch (err) {

            localStorage.setItem("eventRules", null);
            return [];
        }
    };

    function confirmEvent() {
        if (!pendingId) return;

        const ev = events.find(e => String(e.id) === String(pendingId));
        if (!ev) return;

        const requireAccessCode = !!eventsConfig?.require_access_code;

        if (requireAccessCode) {
            setAccessCodeInput("");
            setAccessCodeError("");
            setShowAccessCodeStep(true);
            return;
        }

        finalizeEventSelection(ev);

    }

    async function finalizeEventSelection(ev) {
        if (!ev) return;

        const currentPath = window.location.pathname;
        const shouldRedirectToBranch = String(ev.id) !== String(selectedId) && !currentPath.includes("/branch");

        setSelectedEventData(ev);
        setSelectedId(String(ev.id));

        localStorage.setItem("dataEvent", JSON.stringify(ev));
        localStorage.setItem(SELECTED_KEY, String(ev.id));
        localStorage.setItem(STORAGE_KEY, "1");
        localStorage.removeItem(SKIP_UNTIL_KEY);

        const eventBranches = await getBranchByEvent(ev);

        if (!eventBranches.length) {
            localStorage.removeItem("dataEvent");
            localStorage.removeItem(SELECTED_KEY);
            localStorage.removeItem("branchofficesByEvent");
            setSelectedEventData(null);
            setSelectedId(null);
            setBranchofficesByEvent(null);
            setAccessCodeError(t('events.branchesLoadError'));
            return;
        }

        // Get event rules and save them in localStorage
        await getEventRules(ev.id);


        setHasVisited(true);
        setIsOpen(false);
        setPendingId(null);
        setShowAccessCodeStep(false);
        setAccessCodeInput("");
        setAccessCodeError("");

        showToast(t('events.linkedToast', { name: ev.name }), "#4ade80");

        if (shouldRedirectToBranch) {
            navigate("/branch");
        }
    }

    function skipEvent() {
        localStorage.setItem(STORAGE_KEY, "1");
        localStorage.setItem(SKIP_UNTIL_KEY, String(Date.now() + EVENT_SKIP_MS));
        clearEventSelection();
        setHasVisited(true);
        closeModal();
        navigate("/branch");
    }

    function confirmAccessCodeAndJoin() {
        if (!pendingId) return;

        const ev = events.find(e => String(e.id) === String(pendingId));
        if (!ev) return;

        const expectedCode = String(ev.access_code || "").trim();
        const enteredCode = String(accessCodeInput || "").trim();

        if (!enteredCode) {
            setAccessCodeError(t('events.accessCodeMissing'));
            return;
        }

        if (!expectedCode) {
            setAccessCodeError(t('events.accessCodeNotConfigured'));
            return;
        }

        if (enteredCode !== expectedCode) {
            setAccessCodeError(t('events.accessCodeIncorrect'));
            return;
        }

        finalizeEventSelection(ev);
    }



    /* Which ID to highlight in grid */
    const activeId = pendingId !== null ? pendingId : selectedId;

    const canClose = hasVisited || !!selectedId;
    const showSkipEventOption = false;

    return (
        <>
            {/* <div className="dl-root">       
                {eventsEnabled && (!quote && !code && !PreOrder_id && !keyproduct) && (
                    <button
                        className={`dl-event-btn${selectedEventData ? " has-event" : ""}`}
                        onClick={() => openModal()}
                    >
                        <span className="dl-btn-icon">
                            {selectedEventData ? selectedEventData.logo : <IconCalendar />}
                        </span>
                        {selectedEventData && <span className="dl-live-dot" />}
                        <span className="dl-btn-text">
                            <span className="dl-btn-title">
                                {selectedEventData ? selectedEventData.name : "Eventos activos"}
                            </span>
                            <span className="dl-btn-sub">
                                {selectedEventData
                                    ? (selectedEventData.live ? "En vivo ahora" : selectedEventData.date)
                                    : "Toca para ver y unirte"}
                            </span>
                        </span>
                        <IconChevron />
                    </button>



                )}
            </div> */}
            <div className="dl-root notranslate" translate="no">
                {/* ── Trigger button ── */}
                {eventsEnabled && (!quote && !code && !PreOrder_id && !keyproduct) && (
                    <button className={`dn-event${selectedEventData ? " has-event" : ""}`}
                        translate="no"
                        onClick={() => openModal()} >

                        {selectedEventData && <span className="dl-live-dot" />}
                        <span className="dn-event-content">
                            <span className="d-lg-none d-inline-flex align-items-center">

                                <IconCalendar />
                            </span>

                            <span title={selectedEventData
                                ? (selectedEventData.live ? t('events.liveNow') : "")
                                : t('events.tapToJoin')} className=" d-flex align-items-center d-none d-lg-inline d-md-inline" >
                                {t('events.label')}: {selectedEventData ? selectedEventData.name : t('events.active')}
                            </span>

                        </span>
                    </button>


                )}
            </div>




            {eventsEnabled && (!quote && !code && !PreOrder_id && !keyproduct) && createPortal(
                <div className="dl-root notranslate" translate="no" style={{ "--event-brand": brandColor }}>
                    {/* ── Modal overlay ── */}
                    {isOpen && (
                        <div className="dl-overlay notranslate" translate="no" onClick={handleOverlayClick}>
                            <div className="dl-modal notranslate" translate="no">
                                {/* Header */}
                                <div className="dl-modal-header">
                                    <div>
                                        <p className="dl-eyebrow">{t('events.rightNow')}</p>
                                        <h2 className="dl-modal-title">{t('events.active')}</h2>
                                        <p className="dl-modal-subtitle">{t('events.chooseTitle')}</p>
                                    </div>
                                    {canClose && (
                                        <button className="dl-close-btn" onClick={closeModal}>
                                            <IconClose />
                                        </button>
                                    )}
                                </div>
                                {/* Detalle del evento seleccionado o lista de eventos */}
                                <div className="dl-modal-body">
                                    {selectedEventData ? (
                                        <div className="dl-event-card notranslate active dl-event-detail-modal" translate="no">
                                            <div className="dl-card-img">
                                                <img src={buildCloudinaryImageUrl({ url: selectedEventData.img, width: 800, quality: "auto", type: "image" }) || undefined} alt={selectedEventData.name} onError={e => (e.target.style.opacity = 0)} />
                                                {selectedEventData.live && (
                                                    <div className="dl-live-badge">
                                                        <span className="dl-badge-dot" />
                                                        {t('events.live')}
                                                    </div>
                                                )}
                                                <div className="dl-check">
                                                    <IconCheck size={12} color="white" weight="2.5" />
                                                </div>
                                            </div>
                                            <div className="dl-card-body">
                                                <p className="dl-card-name">{selectedEventData.name}</p>
                                                <p className="dl-card-desc">{selectedEventData.desc}</p>
                                                <div className="dl-card-meta">
                                                    <span className="dl-meta-chip">
                                                        <IconClock /> {selectedEventData.initialDate ? selectedEventData.initialDate.split('T')[0] : ''} - {selectedEventData.maxDate ? selectedEventData.maxDate.split('T')[0] : ''}
                                                    </span>
                                                    <span className="dl-meta-chip">
                                                        <IconPin /> {selectedEventData.location}
                                                    </span>
                                                </div>
                                            </div>

                                        </div>
                                    ) : (
                                        <div className="dl-events-grid">
                                            {events.map(ev => (
                                                <EventCard
                                                    key={ev.id}
                                                    event={ev}
                                                    isActive={String(activeId) === String(ev.id)}
                                                    onSelect={handleSelect}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {/** Access code modal  */}
                                {showAccessCodeStep && (
                                    <div className="dl-access-code-box">
                                        <p className="dl-access-code-title">{t('events.accessCodeRequiredTitle')}</p>
                                        <p className="dl-access-code-subtitle">
                                            {t('events.accessCodeRequiredSubtitle')}
                                        </p>

                                        <input
                                            type="text"
                                            className="dl-access-code-input"
                                            value={accessCodeInput}
                                            onChange={(e) => {
                                                setAccessCodeInput(e.target.value);
                                                if (accessCodeError) setAccessCodeError("");
                                            }}
                                            placeholder={t('events.accessCodePlaceholder')}
                                        />

                                        {accessCodeError && (
                                            <div className="dl-access-code-error">
                                                {accessCodeError}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {/* Footer */}
                                <div className="dl-modal-footer">
                                    {showSkipEventOption && !hasVisited && !selectedId ? (
                                        <button className="dl-skip-link" onClick={skipEvent}>
                                            {t('events.continueWithoutEvent')}
                                        </button>
                                    ) : (
                                        <span />
                                    )}
                                    <div className="dl-footer-actions">
                                        {canClose && (
                                            <button className="dl-btn-cancel" onClick={closeModal}>
                                                {t('common.cancel')}
                                            </button>
                                        )}

                                        {selectedEventData ? <>
                                            <button className={`dl-btn-confirm ready`} onClick={() => { getEvenetsBybusiness(true); showToast(t('events.eventRemovedToast'), "#f87171") }}>
                                                {t('events.removeEvent')}
                                            </button></> : <>
                                            <button
                                                className={`dl-btn-confirm${pendingId ? " ready" : " idle"}`}
                                                onClick={showAccessCodeStep ? confirmAccessCodeAndJoin : confirmEvent}
                                                disabled={!pendingId}
                                            >
                                                <span className="dl-btn-confirm-icon" aria-hidden="true">
                                                    {pendingId ? <IconCheck size={13} color="white" weight="2.5" /> : null}
                                                </span>
                                                <span className="dl-btn-confirm-label">
                                                    {!pendingId ? t('events.chooseEvent') : showAccessCodeStep ? t('events.validateCode') : t('events.joinEvent')}
                                                </span>
                                            </button></>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* ── Toast ── */}
                    <div className={`dl-toast${toast.visible ? "" : " hidden"}`}>
                        <span className="dl-toast-dot" style={{ background: toast.dot }} />
                        {toast.msg}
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}
